import ParallaxTilt from "react-parallax-tilt";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { Lightbox, initTE } from "tw-elements";
import PropTypes from "prop-types";

import { styles } from "../styles";
import { SectionWrapper } from "../hoc";
import { projects } from "../constants";
import { fadeIn, textVariant } from "../utils/motion";
import { useIsMobile } from "../hooks/useIsMobile";
import { useVisibleTime } from "../hooks/useVisibleTime";
import { trackProjectView, trackProjectTime, trackButtonClick } from "../services/trackingService";
import WhatsAppCta from "./ui/WhatsAppCta";
import { WHATSAPP_MESSAGES } from "../constants/whatsapp";

initTE({ Lightbox });

const ProjectCard = ({ index, name, description, tags, image, imageClass, landingPath, isMobile }) => {
  const cardDelay = 0.08 + (index % 3) * 0.05;
  const cardRef = useVisibleTime((seconds) => trackProjectTime(name, seconds));

  return (
    <motion.div
      ref={cardRef}
      className="h-full"
      variants={fadeIn("up", "spring", cardDelay, 0.5)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      onViewportEnter={() => trackProjectView(name)}
    >
      <ParallaxTilt
        tiltEnable={!isMobile}
        tiltMaxAngleX={20}
        tiltMaxAngleY={20}
        scale={1}
        transitionSpeed={450}
        className="glass glass-card glass-surface-tertiary p-5 rounded-2xl w-full md:w-[360px] mx-auto h-full flex flex-col"
      >
        <div className="relative w-full h-[330px] flex-shrink-0">
          <img
            data-te-img={image}
            src={image}
            alt={name}
            className={`w-full h-full rounded-2xl cursor-zoom-in ${imageClass || 'object-cover'}`}
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="mt-5 flex-1 flex flex-col justify-between">
          <div>
            <h3 className="text-foreground font-bold text-[24px]">{name}</h3>
            <p className="mt-2 text-secondary text-[14px]">{description}</p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <p
                key={`${name}-${tag.name}`}
                className={`text-[14px] ${tag.color}`}
              >
                #{tag.name}
              </p>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-3">
            <a
              href="#contacto"
              onClick={() => trackButtonClick(`project_cta_iniciar_proyecto_${name}`, "projects")}
              aria-label={`Iniciar proyecto ${name}`}
              className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-600 to-cyan-500 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-sky-900/40 ring-2 ring-sky-300/50 transition-all duration-300 hover:from-sky-500 hover:to-cyan-400 hover:scale-105 hover:shadow-xl"
            >
              Iniciar Proyecto
            </a>
            {landingPath && (
              <Link
                to={landingPath}
                onClick={() => trackButtonClick(`project_cta_ver_mas_${name}`, "projects")}
                aria-label={`Conocer más sobre ${name}`}
                className="inline-flex items-center justify-center rounded-full border border-sky-400/50 px-5 py-2 text-sm font-bold text-foreground transition-all duration-300 hover:bg-sky-500/10 hover:scale-105"
              >
                Conocer más
              </Link>
            )}
          </div>
        </div>
      </ParallaxTilt>
    </motion.div>
  );
};

const WorksSection = () => {
  const isMobile = useIsMobile();
  return (
    <>
      <motion.div variants={textVariant()}>
        <h2 className={`${styles.sectionHeadText} cursor-default`}>
          Proyectos principales
        </h2>
      </motion.div>

      <div className="w-full flex">
        <motion.p
          variants={fadeIn("", "", 0.1, 1)}
          className="mt-3 text-secondary text-[17px] max-w-5xl leading-[30px]"
        >
          En los siguientes proyectos podrás observar mi
          experiencia desarrollando aplicaciones.
        </motion.p>
      </div>

      <div
        className="mt-20 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-4 justify-items-stretch md:justify-items-center items-stretch w-full"
        data-te-lightbox-init
      >
        {projects.map((project, index) => (
          <ProjectCard key={`project-${index}`} index={index} isMobile={isMobile} {...project} />
        ))}
      </div>

      <motion.div
        variants={fadeIn("up", "", 0.1, 1)}
        className="mt-14 glass glass-card rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
      >
        <p className="text-foreground text-[17px] font-semibold text-center sm:text-left">
          ¿Te gustó alguno? Contame qué necesitás y lo desarrollamos a tu medida.
        </p>
        <WhatsAppCta
          source="projects_whatsapp_cta"
          message={WHATSAPP_MESSAGES.projects}
          label="Hablar por WhatsApp"
          className="shrink-0"
        />
      </motion.div>
    </>
  );
};

ProjectCard.propTypes = {
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired
  })).isRequired,
  image: PropTypes.string.isRequired,
  imageClass: PropTypes.string,
  landingPath: PropTypes.string,
  isMobile: PropTypes.bool,
};

export const Works = SectionWrapper(WorksSection, "proyectos", 0.05);
